import type {
  IComposioCatalogToolkit,
  IOpenConnectorCatalogService,
  ITool,
} from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { resolveComposioToolLogo } from 'pages/tools/components/composio-logos';
import { getComposioConfig, getOpenConnectorConfig } from 'pages/tools/components/tool-detail-utils';

function catalogKey(value: string): string {
  return value
    .replace(/\s*\([^)]*\)\s*/g, ' ')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

export function catalogLogoForTool(
  tool: ITool,
  toolkits: IComposioCatalogToolkit[],
  toolkitSlug = ''
): string | null {
  const fromConfig = getComposioConfig(tool).toolkits[0] ?? '';
  const exactSlug = (toolkitSlug || fromConfig).trim().toUpperCase();
  if (exactSlug) {
    const exact = toolkits.find((entry) => entry.slug.trim().toUpperCase() === exactSlug)?.logo;
    if (exact) return exact;
  }

  const needles = [toolkitSlug, fromConfig, tool.name].map(catalogKey).filter(Boolean);
  let match: { logo: string; length: number } | null = null;

  for (const entry of toolkits) {
    if (!entry.logo) continue;
    const keys = [entry.slug, entry.name].map(catalogKey).filter((key) => key.length >= 4);
    for (const needle of needles) {
      for (const key of keys) {
        const hits = needle === key || needle.includes(key) || key.includes(needle);
        if (hits && (!match || key.length > match.length)) {
          match = { logo: entry.logo, length: key.length };
        }
      }
    }
  }

  return match?.logo ?? null;
}

/** First service of an open connector tool; list responses may omit services, action ids are "<service>.<action>". */
export function openConnectorService(tool: ITool): string {
  const cfg = getOpenConnectorConfig(tool);
  return cfg.services[0] || cfg.actions?.[0]?.split('.')[0] || '';
}

/** Open connector service, or the first provider when list rows omit the config. */
export function openConnectorSlug(tool: ITool): string {
  return openConnectorService(tool) || tool.providers?.[0]?.trim() || '';
}

const serviceKey = (value: string) => value.replace(/[\s_-]+/g, '').toLowerCase();

/** Icon from the open connector catalog whose service or display name matches (OneDrive = one_drive). */
export function openCatalogIcon(
  services: IOpenConnectorCatalogService[] | undefined,
  name: string
): string | null {
  const needle = serviceKey(name);
  return (
    services?.find(
      (entry) => serviceKey(entry.service) === needle || serviceKey(entry.displayName ?? '') === needle
    )?.iconUrl ?? null
  );
}

/**
 * Catalog logo for a connector tool, falling back to its description or name (e.g. "OneDrive C2").
 * Tries exact, then name-prefix, then near matches like "Google Sliders" → "Google Slides".
 */
export function connectorCatalogLogo(
  tool: ITool,
  toolkits: IComposioCatalogToolkit[],
  toolkitSlug = ''
): string | null {
  const lookupKeys = [toolkitSlug, tool.description ?? '', tool.name ?? '']
    .map(catalogKey)
    .filter(Boolean);
  const findEntry = (matches: (entryKey: string, key: string) => boolean) =>
    toolkits.find((entry) =>
      [entry.slug, entry.name ?? ''].map(catalogKey).some((entryKey) =>
        lookupKeys.some((key) => !!entryKey && matches(entryKey, key))
      )
    );
  return (
    (
      findEntry((entryKey, key) => entryKey === key) ||
      findEntry((entryKey, key) => key.startsWith(entryKey)) ||
      // Shared prefix must cover all but the last 2 chars of both keys.
      findEntry((entryKey, key) => {
        let shared = 0;
        while (shared < key.length && key[shared] === entryKey[shared]) shared++;
        return shared >= 4 && shared >= Math.max(key.length, entryKey.length) - 2;
      })
    )?.logo ??
    resolveComposioToolLogo(toolkitSlug, tool.description, tool.name)
  );
}
